/**
 * Tenant onboarding wizard — super_admin one-screen flow.
 * Creates trust + first hostel + first school + first super_admin in one POST.
 */
import React, { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Buildings, House, GraduationCap, UserCircle, Copy, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const initial = {
  trust_name: "", trust_slug: "", trust_pan: "", trust_address: "", trust_phone: "", trust_email: "",
  first_hostel_name: "", first_hostel_capacity: 100,
  first_school_name: "", first_school_capacity: 200,
  admin_name: "", admin_email: "",
  academic_session: "",
};

export default function TenantOnboard() {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.trust_name.trim() || !form.admin_name.trim() || !form.admin_email.trim()) {
      toast.error("Trust name + admin name + admin email are required");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, first_hostel_capacity: Number(form.first_hostel_capacity), first_school_capacity: Number(form.first_school_capacity) };
      const res = await api.post("/admin/onboard", payload);
      setResult(res.data);
      toast.success(`${res.data.trust.name} onboarded`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <DashboardLayout title="Onboarding complete" subtitle={`${result.trust.name} is live. Share the temp password below.`}>
        <div className="vs-card p-6 max-w-2xl space-y-3" data-testid="onboard-result">
          <div className="flex items-center gap-2 text-[var(--vs-success)]">
            <CheckCircle size={18} weight="fill" /> <span className="font-semibold">Tenant created</span>
          </div>
          <div className="vs-divider" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><div className="eyebrow">Trust ID</div><div className="font-mono">{result.trust.id}</div></div>
            <div><div className="eyebrow">Admin email</div><div className="font-mono">{result.admin_email}</div></div>
            <div><div className="eyebrow">Temp password</div>
              <div className="flex items-center gap-2">
                <code className="px-2 py-1 rounded bg-[var(--vs-bg-subtle)] font-mono text-xs" data-testid="onboard-temp-pw">{result.temp_password}</code>
                <button onClick={() => { navigator.clipboard?.writeText(result.temp_password); toast.success("Copied"); }}><Copy size={14} /></button>
              </div>
            </div>
            <div><div className="eyebrow">Welcome email</div><Badge variant="outline">{result.welcome_email_delivery}</Badge></div>
          </div>
          <p className="text-xs text-[var(--vs-text-secondary)]">
            Share the temp password with the new admin. They will be forced to reset it on first login.
          </p>
          <Button onClick={() => { setForm(initial); setResult(null); }} data-testid="onboard-add-another" variant="outline">Onboard another tenant</Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Onboard a new trust" subtitle="Create a tenant + first hostel/school + first super-admin in one go.">
      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-5xl" data-testid="onb-form">
        <div className="vs-card p-5 space-y-3">
          <div className="eyebrow flex items-center gap-1.5"><Buildings size={12} /> Trust</div>
          <div><Label>Trust name *</Label><Input data-testid="onb-trust-name" required value={form.trust_name} onChange={(e) => set("trust_name", e.target.value)} className="bg-white" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Slug (optional)</Label><Input data-testid="onb-trust-slug" value={form.trust_slug} onChange={(e) => set("trust_slug", e.target.value)} className="bg-white font-mono" placeholder="auto from name" /></div>
            <div><Label>PAN</Label><Input data-testid="onb-trust-pan" value={form.trust_pan} onChange={(e) => set("trust_pan", e.target.value.toUpperCase())} className="bg-white font-mono" maxLength={10} /></div>
          </div>
          <div><Label>Registered address</Label><Textarea data-testid="onb-trust-address" value={form.trust_address} onChange={(e) => set("trust_address", e.target.value)} rows={2} className="bg-white" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Phone</Label><Input data-testid="onb-trust-phone" value={form.trust_phone} onChange={(e) => set("trust_phone", e.target.value)} className="bg-white font-mono" /></div>
            <div><Label>Email</Label><Input data-testid="onb-trust-email" type="email" value={form.trust_email} onChange={(e) => set("trust_email", e.target.value)} className="bg-white" /></div>
          </div>
          <div className="vs-divider" />
          <div className="eyebrow flex items-center gap-1.5"><GraduationCap size={12} /> Academic session</div>
          <div><Label>First session</Label><Input data-testid="onb-session" value={form.academic_session} onChange={(e) => set("academic_session", e.target.value)} className="bg-white font-mono" placeholder="2026-27" /></div>
        </div>

        <div className="vs-card p-5 space-y-3">
          <div className="eyebrow flex items-center gap-1.5"><House size={12} /> First hostel (optional)</div>
          <div><Label>Hostel name</Label><Input data-testid="onb-hostel-name" value={form.first_hostel_name} onChange={(e) => set("first_hostel_name", e.target.value)} className="bg-white" /></div>
          <div><Label>Capacity (beds)</Label><Input data-testid="onb-hostel-capacity" type="number" min="0" value={form.first_hostel_capacity} onChange={(e) => set("first_hostel_capacity", e.target.value)} className="bg-white font-mono" /></div>
          <div className="vs-divider" />
          <div className="eyebrow flex items-center gap-1.5"><GraduationCap size={12} /> First school (optional)</div>
          <div><Label>School name</Label><Input data-testid="onb-school-name" value={form.first_school_name} onChange={(e) => set("first_school_name", e.target.value)} className="bg-white" /></div>
          <div><Label>Capacity (seats)</Label><Input data-testid="onb-school-capacity" type="number" min="0" value={form.first_school_capacity} onChange={(e) => set("first_school_capacity", e.target.value)} className="bg-white font-mono" /></div>
          <div className="vs-divider" />
          <div className="eyebrow flex items-center gap-1.5"><UserCircle size={12} /> First super-admin</div>
          <div><Label>Admin name *</Label><Input data-testid="onb-admin-name" required value={form.admin_name} onChange={(e) => set("admin_name", e.target.value)} className="bg-white" /></div>
          <div><Label>Admin email *</Label><Input data-testid="onb-admin-email" required type="email" value={form.admin_email} onChange={(e) => set("admin_email", e.target.value)} className="bg-white" /></div>
          <Button type="submit" disabled={busy} data-testid="onb-submit" className="w-full bg-[var(--vs-secondary)] text-[var(--vs-bg)]">
            {busy ? "Creating tenant…" : "Onboard tenant"}
          </Button>
        </div>
      </form>
    </DashboardLayout>
  );
}
